import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../../../utils/api';
import { evaluateBuyingRules, toTelemetryEvent } from './evaluateBuyingRules.js';

// Buying-rules state for the quote-assessment page: loads the committed config, derives
// the active rule hits reactively from current page state (spec req 5 — no stored
// warning state), and logs 'shown' telemetry (the worker
// dedupes first-fires at (deal_id, item_index, rule_key) grain, so re-posting on every
// hit-set change is safe).
//
// Nothing is evaluated until config AND the deal's categories have loaded — evaluating
// against the transient empty category map would spuriously block Quote Ready and write
// false missing_category first-fires into the telemetry denominator.
export function useBuyingRules(deal, items, itemCategories, categoriesLoaded, itemSubCategories = {}, exclusionsVersion = 0) {
    const [config, setConfig] = useState(null);
    const [configError, setConfigError] = useState(false);
    const [pendingCompExclusions, setPendingCompExclusions] = useState({});
    const dealId = deal?.id;

    useEffect(() => {
        let alive = true;
        let retryTimer = null;
        // Transient failures self-heal: the page-load request burst occasionally
        // loses this one fetch to a connection blip (browser TypeError, no HTTP
        // status) or a 5xx, and without a retry the sticky configError blocks
        // Quote Ready until a manual reload. Genuine rejections (401/403) don't
        // retry — repeating those can't succeed. Same 5s cadence as useAuth.
        const load = () => {
            api.get('/api/buying-rules-config')
                .then(r => { if (alive) { setConfig(r); setConfigError(false); } })
                .catch(e => {
                    console.error('buying-rules config load failed:', e);
                    if (!alive) return;
                    setConfigError(true);
                    if (e instanceof TypeError || e.isRetryable) retryTimer = setTimeout(load, 5000);
                });
        };
        load();
        return () => { alive = false; clearTimeout(retryTimer); };
    }, []);

    // Pending comp-exclusion counts for the comps_stale rule; exclusionsVersion
    // bumps on mark/undo so the confirm dialog sees fresh counts. Degrades to {}
    // on failure — the rule simply stays silent.
    useEffect(() => {
        setPendingCompExclusions({});
        if (!dealId) return;
        let alive = true;
        api.get(`/api/deals/${dealId}/comp-exclusions/pending`)
            .then(r => { if (alive) setPendingCompExclusions(r.pendingByIndex || {}); })
            .catch(() => { if (alive) setPendingCompExclusions({}); });
        return () => { alive = false; };
    }, [dealId, deal?.parsed_items, exclusionsVersion]);

    const ready = !!config && !!categoriesLoaded;
    // items derives from deal.parsed_items every render, so parsed_items is its dep proxy.
    const hits = useMemo(
        () => {
            if (!ready) return [];
            return evaluateBuyingRules(items, itemCategories, config, { itemSubCategories, pendingCompExclusions });
        },
        [deal?.parsed_items, itemCategories, itemSubCategories, config, pendingCompExclusions, ready] // eslint-disable-line react-hooks/exhaustive-deps
    );
    const blockHits = useMemo(() => hits.filter(h => h.severity === 'block'), [hits]);
    const warnHits = useMemo(() => hits.filter(h => h.severity === 'warn'), [hits]);

    const logRuleEvents = useCallback((events, extra = {}) => {
        if (!dealId) return Promise.resolve();
        return api.post(`/api/deals/${dealId}/buying-rule-events`, { events, ...extra });
    }, [dealId]);

    // First-fire telemetry: every active hit is posted as 'shown'; the worker keeps one row.
    const hitsKey = hits.map(h => `${h.itemIndex}:${h.ruleKey}`).sort().join('|');
    useEffect(() => {
        if (!dealId || !hits.length) return;
        logRuleEvents(hits.map(h => toTelemetryEvent(h, 'shown')))
            .catch(e => console.error('buying-rules shown telemetry failed:', e));
    }, [dealId, hitsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    return { hits, blockHits, warnHits, ready, config, configError, logRuleEvents };
}
